// ./src/hooks/useTravels.ts
//
// Data hook for travel records. Mirrors the useExpenses pattern:
// - Race-condition safe list() with monotonic call IDs
// - Ref-based mount-time configuration (no stale closures)
// - Operations return OpResult for full error detail (ApiError field-level support)
// - isLoaded flag to distinguish "not loaded" from "loaded but empty"
// - Keeps stale data visible during reloads (no flash of empty state)
// - Reads userId from useApp() internally

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  Travel,
  TravelService,
  TravelFilter,
  TravelInput,
  Pagination,
  SortItem,
  TravelGetOptions,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE, TRAVEL_STATUSES } from "@/config";

// ============================================
// Types
// ============================================

export interface UseTravelsOptions {
  /**
   * Page size for pagination.
   *
   * This is a **mount-time** configuration value. If you need to change
   * page size after mount, pass it directly to `list()` or `loadMore()`.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new filters, call `list(newFilters)` explicitly.
   *
   * @default { status: [STATUSES.ACTIVE] }
   */
  initialFilters?: Partial<TravelFilter>;

  /**
   * Sort configuration applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply a new sort, pass it directly to `list()`.
   *
   * @default [{ name: "firstDttm", order: "DESC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseTravelsReturn {
  // Data state
  travel: Travel | null;
  travels: Travel[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<TravelFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedTravel: Travel | null;

  // Core operations (matching API method names exactly)
  get: (id: string, options?: TravelGetOptions) => Promise<OpResult>;
  list: (
    filter?: Partial<TravelFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: TravelInput) => Promise<OpResult>;
  update: (id: string, params: TravelInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  pause: (id: string) => Promise<OpResult>;
  resume: (id: string) => Promise<OpResult>;
  isPausing: boolean;
  isResuming: boolean;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedTravel: (travel: Travel | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getTravelById: (id: string | undefined) => Travel | undefined;

  // Filtering helpers
  listActive: (
    filter?: Partial<TravelFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  listCompleted: (
    filter?: Partial<TravelFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "firstDttm", order: "DESC" }];
const DEFAULT_FILTERS: Partial<TravelFilter> = { status: [STATUSES.ACTIVE] };

// ============================================
// Hook Implementation
// ============================================

export const useTravels = (
  options: UseTravelsOptions = {}
): UseTravelsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  // These are read-only after mount. They're used by:
  //   - The userId-change effect (for autoLoad re-fetches)
  //   - loadMore / refresh (pageSize and sort)
  // We use refs (not state) so `list()` and other callbacks don't
  // have stale closures, and so effects don't re-fire on every render.
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters for loadMore
  // so we never read stale `filters` state from a closure.
  const lastQueriedFiltersRef = useRef<Partial<TravelFilter>>(initialFilters);

  // Ref to track the last-queried sort for loadMore/refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses.
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Ref to track current page for loadMore — avoids stale closures
  // when multiple rapid loadMore calls happen before state settles.
  const currentPageRef = useRef(1);

  // Data state
  const [travel, setTravel] = useState<Travel | null>(null);
  const [travels, setTravels] = useState<Travel[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried (for display/debugging)
  const [filters, setFilters] =
    useState<Partial<TravelFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [selectedTravel, setSelectedTravel] = useState<Travel | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && travels.length === 0,
    [isLoaded, travels.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: Travel) => {
    setTravels((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setTravels((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setTravels((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Pause tracking
  const pause = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Travel ID is required for pausing"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsPausing(true);
      setLastError(null);

      try {
        const result = await TravelService.pause(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Travel[])[0];
          setTravel(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to pause tracking"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to pause tracking"));
        return result;
      } finally {
        setIsPausing(false);
      }
    },
    [updateOrAddInList]
  );

  // Resume tracking
  const resume = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Travel ID is required for resuming"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsResuming(true);
      setLastError(null);

      try {
        const result = await TravelService.resume(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Travel[])[0];
          setTravel(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to resume tracking"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to resume tracking"));
        return result;
      } finally {
        setIsResuming(false);
      }
    },
    [updateOrAddInList]
  );

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setTravel(null);
    setTravels([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setIsPausing(false);
    setIsResuming(false);
    setSelectedTravel(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single travel
  const get = useCallback(
    async (id: string, options?: TravelGetOptions): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await TravelService.get(id, options);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as Travel[])[0];
          setTravel(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load travel"));
        setTravel(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load travel"));
        setTravel(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List travels.
   * - shouldReset=true: replaces travels array (page 1 load / filter change)
   * - shouldReset=false: appends to existing travels (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   *
   * Note: This callback has an empty dependency array because it only accesses
   * external values via refs (pageSizeRef, lastQueriedFiltersRef, etc.) and
   * React state setters (setLoading, setTravels, etc.) whose identity is
   * guaranteed stable by React across renders.
   */
  const list = useCallback(
    async (
      filter: Partial<TravelFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Don't clear travels here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
        currentPageRef.current = 1;
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore and refresh can use
      // them via refs — avoids stale closures.
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await TravelService.list(
          filter as TravelFilter,
          paginationParams,
          sortParams
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as Travel[])
            : [];

          if (isAppending) {
            setTravels((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setTravels(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          currentPageRef.current = targetPage;
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load travels"));
        setIsLoaded(true);
        if (!isAppending) setTravels([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load travels"));
        setIsLoaded(true);
        if (!isAppending) setTravels([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // Get many travels by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await TravelService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as Travel[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load travels"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load travels"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Create travel
  const create = useCallback(
    async (params: TravelInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await TravelService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as Travel[])[0];
          setTravel(newItem);
          setTravels((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create travel"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create travel"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update travel
  const update = useCallback(
    async (id: string, params: TravelInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Travel ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await TravelService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as Travel[])[0];
          setTravel(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update travel"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update travel"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Remove travel
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Travel ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await TravelService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setTravel(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove travel"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove travel"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many travels
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one travel ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await TravelService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setTravel(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove travels"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to remove travels")
        );
        return result;
      }
    },
    [removeManyFromList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more travels
  // Uses refs for filters, sort, and current page to avoid stale closures.
  // Reading currentPage from a ref (instead of state) ensures that rapid
  // successive loadMore calls always compute the correct next page, even
  // if React hasn't flushed the state update from the previous call yet.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPageRef.current + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore]);

  // Refresh travels (reset to page 1)
  // Uses refs so it always picks up the last-queried filters/sort.
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  // Lookup helper
  const getTravelById = useCallback(
    (id: string | undefined): Travel | undefined => {
      if (!id) return undefined;
      return travels.find((item) => item.id === id);
    },
    [travels]
  );

  // ==========================================
  // Filtering Helpers
  // ==========================================

  // Convenience method to list only active (in-progress) travels
  const listActive = useCallback(
    async (
      filter: Partial<TravelFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = false
    ): Promise<OpResult> => {
      const activeFilter: Partial<TravelFilter> = {
        ...filter,
        status: [TRAVEL_STATUSES.IN_PROGRESS],
      };
      return list(activeFilter, pagination, sortByParams, shouldReset);
    },
    [list]
  );

  // Convenience method to list only completed travels
  const listCompleted = useCallback(
    async (
      filter: Partial<TravelFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = false
    ): Promise<OpResult> => {
      const completedFilter: Partial<TravelFilter> = {
        ...filter,
        status: [TRAVEL_STATUSES.COMPLETED],
      };
      return list(completedFilter, pagination, sortByParams, shouldReset);
    },
    [list]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      travel,
      travels,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      selectedTravel,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      pause,
      resume,
      isPausing,
      isResuming,

      // Additional convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedTravel,

      // Helper operations
      reset,

      // Lookup helpers
      getTravelById,

      // Filtering helpers
      listActive,
      listCompleted,

      // Error handling
      lastError,
      clearError,
    }),
    [
      travel,
      travels,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      selectedTravel,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      pause,
      resume,
      isPausing,
      isResuming,
      loadMore,
      refresh,
      getTravelById,
      listActive,
      listCompleted,
      lastError,
      clearError,
      reset,
    ]
  );
};